/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Action, ActionCreators } from './actions';
import FlightLeg, { FlightLegInfo } from '../../shared/entities/flightLeg';
import FlightLegServiceAlerts from '../../shared/entities/flightLegServiceAlerts';
import { OverlaysContextsEnum } from '../maps/reducer';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';
import { Action as GlobalAction, ActionCreators as GlobalActionCreators } from '../actions';

interface FlightLegMapContent {
  readonly selectedOverlaidMissionPlanId?: number;
  readonly selectedOverlaidFlightLegId?: number;
}

interface FlightLegMapContext {
  uiState: { [key in OverlaysContextsEnum]: FlightLegMapContent };
}

export interface FlightLegUploadSuccessResponse {
  fileName: string;
  error: string | undefined;
  flightLeg: FlightLegServiceAlerts | undefined;
}
export interface State {
  readonly flightLegs: { [id: number]: FlightLeg };
  readonly isRequestingFlightLegs: boolean;
  readonly requestingFlightLegsError?: string;
  readonly isRequestingFlightLegsPO: boolean;

  readonly flightLegsInfo: { [id: number]: FlightLegInfo };
  readonly isRequestingFlightLegsInfo: boolean;
  readonly requestingFlightLegsInfoSuccess?: boolean;
  readonly requestingFlightLegsInfoError?: string;

  readonly uploadedFlightLegs: FlightLegUploadSuccessResponse[];
  readonly failedToParseFlightLegs: FlightLegUploadSuccessResponse[];
  readonly uploadingFlightLeg: boolean;
  readonly uploadFlightLegError?: string;

  readonly updatingFlightLegsOrder: boolean;
  readonly updateFlightLegsOrderError?: string;

  readonly deletingFlightLeg: boolean;
  readonly deleteFlightLegSuccess?: boolean;
  readonly deleteFlightLegError?: string;
  readonly deleteLegConfirmation: boolean;
  readonly flightLegToDelete?: FlightLegInfo;

  readonly mapContext: { [mapId: string]: FlightLegMapContext };
  currentContext: OverlaysContextsEnum;

  readonly isUpdatingTakeoffTime: boolean;
  readonly updateTakeoffTimeError?: string;
}

export const initialMapsState: { [mapId: string]: FlightLegMapContext } = {
  [GOOGLE_MAP_INSTANCE_KEY]: {
    uiState: {
      [OverlaysContextsEnum.FLEET_VIEW]: {},
      [OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL]: {},
      [OverlaysContextsEnum.FTV]: {},
      [OverlaysContextsEnum.FTV_EXPORT]: {},
      [OverlaysContextsEnum.PERFORMANCE_OUTLOOK]: {},
      [OverlaysContextsEnum.PERFORMANCE_OUTLOOK_EXPORT]: {},
      [OverlaysContextsEnum.TRANSPORT_MONITORING]: {},
      [OverlaysContextsEnum.FULL_SPACE_VEHICLE_VIEW]: {},
    },
  },
};

export const initialFlightLegsState: State = {
  flightLegs: {},
  isRequestingFlightLegs: false,
  requestingFlightLegsError: undefined,
  isRequestingFlightLegsPO: false,

  flightLegsInfo: {},
  isRequestingFlightLegsInfo: false,
  requestingFlightLegsInfoError: undefined,

  uploadedFlightLegs: [],
  failedToParseFlightLegs: [],
  uploadingFlightLeg: false,
  uploadFlightLegError: undefined,

  updatingFlightLegsOrder: false,
  updateFlightLegsOrderError: undefined,

  deletingFlightLeg: false,
  deleteFlightLegSuccess: undefined,
  deleteFlightLegError: undefined,
  deleteLegConfirmation: false,
  flightLegToDelete: undefined,

  mapContext: initialMapsState,
  currentContext: OverlaysContextsEnum.FLEET_VIEW,

  isUpdatingTakeoffTime: false,
  updateTakeoffTimeError: undefined,
};

// Reducer
export default function reducer(state: State = initialFlightLegsState, action: Action | GlobalAction): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.UpdateTakeoffTime.type:
      partialState = {
        isUpdatingTakeoffTime: true,
        updateTakeoffTimeError: undefined,
      };
      break;
    case ActionCreators.UpdateTakeoffTimeSuccess.type:
      partialState = {
        isUpdatingTakeoffTime: false,
        updateTakeoffTimeError: undefined,
      };
      break;
    case ActionCreators.UpdateTakeoffTimeError.type:
      partialState = {
        isUpdatingTakeoffTime: false,
        updateTakeoffTimeError: action.payload,
      };
      break;
    case ActionCreators.ClearTakeoffTimeError.type:
      partialState = {
        updateTakeoffTimeError: undefined,
      };
      break;
    case ActionCreators.RequestFlightLegs.type:
      partialState = {
        isRequestingFlightLegs: true,
        requestingFlightLegsError: undefined,
        isRequestingFlightLegsPO: true,
      };
      break;

    case ActionCreators.ReceivedFlightLegsSuccess.type: {
      const flightLegs: FlightLeg[] = action.payload.response.slice();
      const compiledFlightLegs: { [id: number]: FlightLeg } = {};
      flightLegs.forEach((flightLeg: FlightLeg) => {
        compiledFlightLegs[flightLeg.id] = flightLeg;
      });
      partialState = {
        flightLegs: compiledFlightLegs,
        requestingFlightLegsError: undefined,
        isRequestingFlightLegs: false,
        isRequestingFlightLegsPO: false,
      };
      break;
    }
    case ActionCreators.ReceivedFlightLegsError.type:
      partialState = {
        requestingFlightLegsError: action.payload,
        isRequestingFlightLegs: false,
        isRequestingFlightLegsPO: false,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegsError.type:
      partialState = {
        requestingFlightLegsError: undefined,
      };
      break;
    case ActionCreators.RequestFlightLegsInfo.type:
      partialState = {
        isRequestingFlightLegsInfo: true,
        requestingFlightLegsInfoSuccess: undefined,
        requestingFlightLegsInfoError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegsInfoSuccess.type:
      const flightLegs: FlightLegInfo[] = action.payload.response;
      const compiledFlightLegs: { [id: number]: FlightLegInfo } = {};
      flightLegs.forEach((flightLeg: FlightLegInfo) => {
        compiledFlightLegs[flightLeg.id] = flightLeg;
      });
      partialState = {
        flightLegsInfo: compiledFlightLegs,
        isRequestingFlightLegsInfo: false,
        requestingFlightLegsInfoSuccess: true,
        requestingFlightLegsInfoError: undefined,
      };
      break;
    case ActionCreators.ClearFlightLegsInfo.type:
      partialState = {
        flightLegsInfo: {},
      };
      break;
    case ActionCreators.ReceivedFlightLegsInfoError.type:
      partialState = {
        requestingFlightLegsInfoError: action.payload,
        requestingFlightLegsInfoSuccess: false,
        isRequestingFlightLegsInfo: false,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegsInfoSuccess.type:
      partialState = {
        requestingFlightLegsInfoSuccess: undefined,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegsInfoError.type:
      partialState = {
        requestingFlightLegsInfoError: undefined,
      };
      break;
    case ActionCreators.RequestFlightLegUpload.type:
      partialState = {
        uploadingFlightLeg: true,
        uploadedFlightLegs: [],
        uploadFlightLegError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegUploadSuccess.type:
      const uploadedFlightLegs: FlightLegUploadSuccessResponse[] = action.payload.response;
      const newFailedToParseFlightLegs = uploadedFlightLegs.filter(flightLeg => flightLeg.error);
      const newParsedFlightLegs = uploadedFlightLegs.filter(flightLeg => !flightLeg.error);

      // 1. Remove any previously failed legs that are parsed successfully now.
      // 2. Add new failed legs only if they are not already in the failed list.
      const failedToParseFlightLegs = [
        ...state.failedToParseFlightLegs.filter(
          failedLeg => !newParsedFlightLegs.some(parsedLeg => parsedLeg.fileName === failedLeg.fileName),
        ),
        ...newFailedToParseFlightLegs.filter(
          flightLeg => !state.failedToParseFlightLegs.some(failedLeg => failedLeg.fileName === flightLeg.fileName),
        ),
      ];

      partialState = {
        uploadedFlightLegs,
        failedToParseFlightLegs,
        uploadingFlightLeg: false,
        uploadFlightLegError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegUploadError.type:
      partialState = {
        uploadFlightLegError: action.payload,
        uploadingFlightLeg: false,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegUploadError.type:
      partialState = {
        uploadFlightLegError: undefined,
      };
      break;
    case ActionCreators.RequestFlightLegOrder.type:
      const flightLegIds: string[] = action.payload.idList;
      let flightLegsInfo = { ...state.flightLegsInfo };
      const updatedFlightLegsInfo: { [id: number]: FlightLegInfo } = {};

      if (Object.keys(flightLegsInfo).length > 0 && flightLegIds) {
        flightLegIds.forEach((id: string, index: number) => {
          let tempLeg = flightLegsInfo[parseInt(id)];
          tempLeg.legOrder = index + 1;

          updatedFlightLegsInfo[tempLeg.id] = tempLeg;
        });
        partialState = {
          updatingFlightLegsOrder: true,
          updateFlightLegsOrderError: undefined,
          flightLegsInfo: updatedFlightLegsInfo,
        };
        break;
      }
      partialState = {
        updatingFlightLegsOrder: true,
        updateFlightLegsOrderError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegOrderSuccess.type:
      partialState = {
        updatingFlightLegsOrder: false,
        updateFlightLegsOrderError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegOrderError.type:
      partialState = {
        updateFlightLegsOrderError: action.payload,
        updatingFlightLegsOrder: false,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegOrderError.type:
      partialState = {
        updateFlightLegsOrderError: undefined,
      };
      break;
    case ActionCreators.RequestFlightLegDelete.type:
      const idToDelete = action.payload.flightLegId;
      const legOrderCutoff = state.flightLegsInfo[action.payload.flightLegId].legOrder;
      let updatedFlightLegInfo = { ...state.flightLegsInfo };

      // Delete key value pair
      delete updatedFlightLegInfo[idToDelete];

      // Decrement leg order of relevant flight legs
      Object.keys(updatedFlightLegInfo).forEach((flightLegId: string) => {
        const legOrder = updatedFlightLegInfo[parseInt(flightLegId)].legOrder;
        if (legOrder > legOrderCutoff) {
          updatedFlightLegInfo[parseInt(flightLegId)].legOrder = legOrder - 1;
        }
      });

      partialState = {
        deletingFlightLeg: true,
        deleteFlightLegError: undefined,
        flightLegsInfo: updatedFlightLegInfo,
      };
      break;
    case ActionCreators.ReceivedFlightLegDeleteSuccess.type:
      partialState = {
        deletingFlightLeg: false,
        deleteFlightLegSuccess: true,
        deleteFlightLegError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlightLegDeleteError.type:
      partialState = {
        deleteFlightLegError: action.payload,
        deletingFlightLeg: false,
      };
      break;
    case ActionCreators.ClearReceivedFlightLegDeleteStatus.type:
      partialState = {
        deleteFlightLegSuccess: undefined,
        deleteFlightLegError: undefined,
      };
      break;
    case ActionCreators.DeleteLegConfirmation.type:
      partialState = {
        deleteLegConfirmation: action.payload.visible,
      };
      break;
    case ActionCreators.SetFlightLegToDelete.type:
      partialState = {
        flightLegToDelete: action.payload.flightLeg,
      };
      break;
    case ActionCreators.ClearRelevantFlightInfoFields.type:
      partialState = {
        flightLegsInfo: {},
        requestingFlightLegsInfoError: undefined,

        uploadedFlightLegs: [],
        failedToParseFlightLegs: [],
        uploadFlightLegError: undefined,

        updateFlightLegsOrderError: undefined,

        deleteFlightLegError: undefined,
      };
      break;

    case ActionCreators.SetSelectedOverlaidMissionPlanId.type: {
      return {
        ...state,
        mapContext: {
          [action.payload.mapId]: {
            ...state.mapContext[action.payload.mapId],
            uiState: {
              ...state.mapContext[action.payload.mapId]?.uiState,
              [state.currentContext]: {
                ...state.mapContext[action.payload.mapId]?.uiState[state.currentContext],
                selectedOverlaidMissionPlanId: action.payload.id,
              },
            },
          },
        },
      };
    }

    case ActionCreators.SetSelectedOverlaidFlightLegId.type: {
      return {
        ...state,
        mapContext: {
          [action.payload.mapId]: {
            ...state.mapContext[action.payload.mapId],
            uiState: {
              ...state.mapContext[action.payload.mapId]?.uiState,
              [state.currentContext]: {
                ...state.mapContext[action.payload.mapId]?.uiState[state.currentContext],
                selectedOverlaidFlightLegId: action.payload.id,
              },
            },
          },
        },
      };
    }

    case ActionCreators.ClearMissionData.type: {
      return {
        ...state,
        mapContext: {
          [action.payload.mapId]: {
            ...state.mapContext[action.payload.mapId],
            uiState: {
              ...state.mapContext[action.payload.mapId]?.uiState,
              [state.currentContext]: {
                ...state.mapContext[action.payload.mapId]?.uiState[state.currentContext],
                selectedOverlaidFlightLegId: undefined,
                selectedOverlaidMissionPlanId: undefined,
              },
            },
          },
        },
      };
    }
    case GlobalActionCreators.SwitchOverlaysContext.type: {
      return {
        ...state,
        currentContext: action.payload,
      };
    }
    case ActionCreators.removeFailedToParseFlightLeg.type: {
      const { fileName, isClearAll } = action.payload;
      let updatedFailedToParseFlightLegs = state.failedToParseFlightLegs;

      if (isClearAll) {
        updatedFailedToParseFlightLegs = [];
      } else if (fileName) {
        updatedFailedToParseFlightLegs = state.failedToParseFlightLegs.filter(leg => leg.fileName !== fileName);
      }

      return {
        ...state,
        failedToParseFlightLegs: updatedFailedToParseFlightLegs,
      };
    }
    case ActionCreators.UpdateFailedToParseFlightLegs.type: {
      const newFailedLegs = action.payload;
      // filter out any new failed legs that are already in the failed list
      const updatedFailedToParseFlightLegs = state.failedToParseFlightLegs.filter(
        leg => !newFailedLegs.some(newLeg => newLeg.fileName === leg.fileName),
      );
      return {
        ...state,
        failedToParseFlightLegs: [...updatedFailedToParseFlightLegs, ...newFailedLegs],
      };
    }
    default:
      return state;
  }
  return { ...state, ...partialState };
}
